import React, { useState } from 'react'
import { Tokenomics, TokenomicsField } from '@/components/Context'
import Button from '@/components/Button'
import { defaultTokenomics, MINIMAL_FEE_PERCENTAGE } from '@/components/create/CreateOverview'

interface TokenomicsFormProps {
    tokenomics: Tokenomics
    onChange: (updatedTokenomics: Tokenomics) => void
}

const mapKey = (key: string): string => {
    switch (key) {
        case 'fee':
            return 'Fee'
        case 'owner':
            return 'Owner'
        case 'creator':
            return 'Creator'
        case 'burn':
            return `Buy & Burn`
        case 'airdrop':
            return 'Airdrop'
        case 'votes':
            return 'Votes'
        case 'claims':
            return 'Claims'
        case 'treasury':
            return 'Treasury'
        default:
            break
    }
    return ''
}

const TokenomicsForm: React.FC<TokenomicsFormProps> = ({ tokenomics, onChange }) => {
    const [values, setValues] = useState<Tokenomics>(tokenomics)
    const [fixedFields, setFixedFields] = useState<Record<string, boolean>>(() => {
        const initialFixedFields: Record<string, boolean> = {}
        Object.keys(tokenomics).forEach((key) => {
            if (
                key !== 'artState' &&
                key !== 'burnToken' &&
                key !== 'owner' &&
                key !== 'burnAddress' &&
                key !== 'creatorAddress' &&
                key !== 'treasuryAddress'
            ) {
                initialFixedFields[key] = true
            }
        })
        return initialFixedFields
    })

    const ensureNonNegativeAndWithinLimits = (field: TokenomicsField, value: number): number => {
        if (field === 'fee') {
            return Math.max(MINIMAL_FEE_PERCENTAGE, Math.min(100, value))
        }
        return Math.max(0, Math.min(100 - MINIMAL_FEE_PERCENTAGE, value))
    }

    const fixedTotal = Object.keys(values)
        .filter(
            (key) =>
                key !== 'artState' &&
                key !== 'burnToken' &&
                key !== 'owner' &&
                key !== 'burnAddress' &&
                key !== 'creatorAddress' &&
                key !== 'treasuryAddress',
        )
        .reduce((acc, key) => acc + values[key as keyof TokenomicsField], 0)

    const getMaxValueForField = (field: TokenomicsField): number => {
        const fixedTotal = Object.keys(values)
            .filter((key) => fixedFields[key])
            .reduce((acc, key) => acc + values[key as keyof TokenomicsField], 0)
        return Math.max(0, 100 - (field === 'fee' ? 0 : MINIMAL_FEE_PERCENTAGE) - fixedTotal)
    }

    const handleSliderChange = (field: TokenomicsField, value: number) => {
        if (fixedFields[field]) return

        value = ensureNonNegativeAndWithinLimits(field, value)

        const otherFields = Object.keys(values)
            .filter(
                (key) =>
                    key !== field &&
                    key !== 'artState' &&
                    key !== 'burnToken' &&
                    key !== 'owner' &&
                    key !== 'burnAddress' &&
                    key !== 'creatorAddress' &&
                    key !== 'treasuryAddress' &&
                    !fixedFields[key],
            )
            .map(
                (key) =>
                    key as keyof Omit<
                        Tokenomics,
                        'artState' | 'burnToken' | 'owner' | 'burnAddress' | 'creatorAddress' | 'treasuryAddress'
                    >,
            )

        const fixedTotal = Object.keys(values)
            .filter((key) => fixedFields[key])
            .reduce((acc, key) => acc + (values[key as keyof TokenomicsField] as number), 0)

        let totalOther = otherFields.reduce((acc, key) => acc + values[key], 0)
        let total = fixedTotal + totalOther + value

        if (total > 100) {
            const excess = total - (100 - MINIMAL_FEE_PERCENTAGE)
            const adjustmentFactor = totalOther === 0 ? 0 : (totalOther - excess) / totalOther

            otherFields.forEach((key) => {
                const adjustedValue = parseFloat((values[key] * adjustmentFactor).toFixed(2))
                values[key] = ensureNonNegativeAndWithinLimits(key, adjustedValue)
            })

            totalOther = otherFields.reduce((acc, key) => acc + values[key], 0)
            total = fixedTotal + totalOther + value
        }

        const updatedValues = { ...values, [field]: value }

        setValues(updatedValues)
        onChange(updatedValues)
    }

    const handleInputChange = (field: TokenomicsField, value: number) => {
        handleSliderChange(field, value)
    }

    const handleCheckboxChange = (field: TokenomicsField) => {
        setFixedFields({
            ...fixedFields,
            [field]: !fixedFields[field],
        })
    }

    return (
        <div>
            <div className={'menu-wrapper-row'}>
                <Button
                    btnName={`Default`}
                    classStyles="nav__button blue"
                    handleClick={() => {
                        setValues(defaultTokenomics)
                    }}
                    cancelBg={undefined}
                />
                <span></span>
                <span
                    className={fixedTotal !== 100 ? 'red-text' : 'light-gray'}
                >{`Total: ${fixedTotal.toFixed()}%`}</span>
                <span></span>
            </div>
            {Object.keys(values).map((key) => {
                if (
                    key === 'artState' ||
                    key === 'burnToken' ||
                    key === 'owner' ||
                    key === 'burnAddress' ||
                    key === 'creatorAddress' ||
                    key === 'treasuryAddress'
                )
                    return null // Skip non-numeric

                // fields
                const numericKey = key as keyof Omit<
                    Tokenomics,
                    'artState' | 'burnToken' | 'owner' | 'burnAddress' | 'treasuryAddress' | 'creatorAddress'
                >
                const maxValue = getMaxValueForField(numericKey)
                return (
                    <div key={key} className="tokenomics-row">
                        <div className="label-column gray">
                            <label>{mapKey(key)}</label>
                        </div>
                        <div className="input-column padding-zero">
                            <input
                                type="range"
                                min={numericKey === 'fee' ? MINIMAL_FEE_PERCENTAGE : 0}
                                max={fixedFields[numericKey] ? undefined : maxValue}
                                step="0.1"
                                value={values[numericKey]}
                                onChange={(e) => handleSliderChange(numericKey, parseFloat(e.target.value))}
                                disabled={fixedFields[numericKey]}
                            />
                        </div>
                        <div className="value-column">
                            <input
                                type="number"
                                className="number-input"
                                value={values[numericKey]}
                                onChange={(e) => handleInputChange(numericKey, parseFloat(e.target.value))}
                                disabled={fixedFields[numericKey]}
                                min="0"
                                max={fixedFields[numericKey] ? undefined : maxValue}
                            />
                        </div>
                        <div className="checkbox-column">
                            <input
                                type="checkbox"
                                checked={fixedFields[numericKey]}
                                onChange={() => handleCheckboxChange(numericKey)}
                            />
                        </div>
                    </div>
                )
            })}

            <div className={'flex-row form-as-class align-right'}>
                <span>{`Buy & Burn Token`}</span>
                <input
                    className="width300"
                    placeholder={`${values.burn}% CRAZY will be burned. No token buying.`}
                    value={values.burnToken}
                    onChange={(e) => {
                        setValues({ ...values, burnToken: e.target.value })
                    }}
                />
            </div>
        </div>
    )
}

export default TokenomicsForm
